import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, MessageSquareText, X } from "lucide-react";
import { NAV_LINKS, smsLink } from "@/data/site";
import { useSectionNav } from "@/hooks/useSectionNav";

const Brand = ({ onClick }) => (
  <button onClick={onClick} className="flex items-center gap-3 text-left" data-testid="brand-logo" aria-label="Freedom Mobile Auto Detailing — back to top">
    <span className="flex h-9 w-9 items-center justify-center border border-blue-500/50 font-display text-xl font-bold text-white">
      F
    </span>
    <span className="leading-none">
      <span className="block font-display text-xl font-bold uppercase tracking-[0.08em] text-white">Freedom</span>
      <span className="mt-1 block font-mono text-[9px] font-semibold uppercase tracking-[0.3em] text-slate-400">
        Mobile Auto Detailing
      </span>
    </span>
  </button>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const go = useSectionNav();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const nav = (id) => {
    setOpen(false);
    go(id);
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-colors duration-300 ${
        scrolled || open ? "border-b border-white/10 bg-obsidian/90 backdrop-blur-md" : "border-b border-transparent bg-obsidian/40 backdrop-blur-sm"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-5 sm:px-8">
        <Brand onClick={() => nav("home")} />
        <nav className="hidden items-center gap-7 lg:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <button
              key={l.id}
              onClick={() => nav(l.id)}
              className="font-mono text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-300 transition-colors duration-200 hover:text-white"
              data-testid={`nav-link-${l.id}`}
            >
              {l.label}
            </button>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href={smsLink()} className="hidden items-center gap-2 border border-white/20 px-4 py-2.5 font-display text-sm font-semibold uppercase tracking-wider text-white transition-colors duration-200 hover:border-white/50 sm:inline-flex" data-testid="header-text-us-button">
            <MessageSquareText size={15} strokeWidth={2.2} />
            Text Us
          </a>
          <button onClick={() => nav("quote")} className="hidden bg-steel px-5 py-2.5 font-display text-sm font-semibold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-blue-500 sm:block" data-testid="header-quote-button">
            Get a Quote
          </button>
          <button onClick={() => setOpen(!open)} className="flex h-10 w-10 items-center justify-center border border-white/20 text-white lg:hidden" aria-label={open ? "Close menu" : "Open menu"} aria-expanded={open} data-testid="mobile-menu-toggle">
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden border-t border-white/10 bg-obsidian/95 backdrop-blur-md lg:hidden"
            aria-label="Mobile"
            data-testid="mobile-menu"
          >
            <div className="space-y-1 px-5 py-6">
              {NAV_LINKS.map((l, i) => (
                <motion.button
                  key={l.id}
                  initial={{ opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                  onClick={() => nav(l.id)}
                  className="block w-full py-2.5 text-left font-display text-2xl font-semibold uppercase tracking-wide text-slate-200 transition-colors hover:text-blue-400"
                  data-testid={`mobile-nav-link-${l.id}`}
                >
                  {l.label}
                </motion.button>
              ))}
              <div className="flex gap-3 pt-5">
                <a href={smsLink()} className="btn-outline flex-1 !px-4 !py-3 text-base" data-testid="mobile-menu-text-us">
                  Text Us
                </a>
                <button onClick={() => nav("quote")} className="btn-primary flex-1 !px-4 !py-3 text-base" data-testid="mobile-menu-quote-button">
                  Get a Quote
                </button>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};

export default Header;
